/* eslint-disable no-unused-vars */
import { assign, createMachine } from "xstate";

import { CheckoutItem, CheckoutUser, OrderAmount } from "../types/checkout";
import { Order, OrderAddress } from "../types/order";
import { AddrType, ShipName, ShipType } from "../types/shipAddr";
import { PaymentSession } from "../types/pay";

export interface Country {
  id: string;
  name: string;
  code: string;
  iso: string;
}

export interface CheckoutContext {
  customer: CheckoutUser | null;
  addr: OrderAddress | null;
  shipCountry: Country | any;
  items: CheckoutItem[] | null;
  total: OrderAmount | null;
  shipId: string | null;
  shipName: ShipName | null;
  shipType: ShipType | null;
  addrType: AddrType | null;
  orderId: string | null;
  paymentSession: PaymentSession | any;
  orderNote: string;
}

const assignAddrDetails = assign({
  addr: (context, event: any) => event.addr,
});

const assignUpdatedTotal = assign({
  total: (context, event: any) => event.total,
});

const assignDefaults = assign({
  customer: (context, event: any) => event.customer,
  addr: (context, event: any) => event.addr,
  addrType: (context, event: any) => event.addrType,
  items: (context, event: any) => event.items,
  paymentSession: (context, event: any) => event.paymentSession,
  shipCountry: (context, event: any) => event.shipCountry,
  shipId: (context, event: any) => event.shipId,
  shipName: (context, event: any) => event.shipName,
  shipType: (context, event: any) => event.shipType,
  total: (context, event: any) => event.total,
});

const assignShipDetails = assign({
  addr: (context, event: any) => event.addr,
  addrType: (context, event: any) => event.addrType,
  shipId: (context, event: any) => event.shipId,
  shipName: (context, event: any) => event.shipName,
  shipType: (context, event: any) => event.shipType,
});

const assignPaymentMethod = assign({
  paymentSession: (context, event: any) => {
    return { payOptionId: event.payOptionId, paymentType: event.paymentType };
  },
});

const assignNote = assign({
  orderNote: (context, event: any) => {
    return event.note;
  },
});

// returns every state to default except and add paymentData
const clearXState = assign({
  addr: (context, event: any) => null,
  addrType: (context, event: any) => null,
  items: (context, event: any) => null,
  orderId: (context, event: any) => null,
  orderNote: (context, event: any) => null,
  paymentSession: (context, event: any) => null,
  shipCountry: (context, event: any) => null,
  shipId: (context, event: any) => null,
  shipName: (context, event: any) => null,
  shipType: (context, event: any) => null,
  total: (context, event: any) => null,
});

export const onlineCheckoutMachine =
  /** @xstate-layout N4IgpgJg5mDOIC5QGMAWZkGsD2BXALgHQCWAdsfgMQDKAogDK0DCAKgPoCCAIlwEoDaABgC6iUAAdssCsWykxIAB6IAjAA5BAdkIAWAMwBWHYIBMgtWs1qTAGhABPVWpW6AbK5MBOTZp2aTVpp6AL7BdmgYOASEsGAANhj4HBAQAE6U3HxsdIystFxCokggktL4svLFygiGrjqE3joGBp4Whoaado4I+oKEdc4Gvibq+gah4ehYeESxCchJKakx8YlMeKT4qfY0DMzsTADyAKoAciy8AJqFCqUycgrVJjrPhHo66u96n96uXYh6TyeQhBDyuNSud4fII6CYgCLTaJzRLJNIreb4AAKqTAADdUekcvtsgAxG7FO7lB5VRBeQQuQaaVyCLR1AL-GqePSENRDQQGFSeEzfTwvVxwhFRWarBYE9GJbF4gm7XLsagsQ68WhsTEASSYAGljpjyRIpPdKqAnp56TyVENmayXp0HIgjH0dDpnDadFy+eKwvCplL5bKlqGsTj8UsVcSAEIcaj600lc1Uy1KWk2hn2pkspnOjnPbQmVwGCzWTxDYVWCXBmYRuXIhaK6NpSgAIwAhlgU5SKo9aQZSzyPoM9NZnPoOe9gc93HUVBO6ly65EG83FmiwJswKlTmAAO7K7u9kS3NMDmk1QQvfovIwmAx6Vw2tQcwWuEFMvTmXmaW9WjUNdEWlDEmxlfBqAAMxPHtMD7S9qStAFbxMe8nwfF83xnfQ3lcXw-GdMUTBAkNNwgjFqHwbAcUxYgsFwcRO3gxCyivFCbzvNlH2fV9zA5L1gSrCE9CfMTjBaMiG3ELt7AAWx3MCFTkxTNkoABZWgWAACUOLhsj2PICnPCkkIzJ5fXQkYn2HKwPnLDk1G8BoDEESECJaZoXmk6JZIUpTCGQHEu3KUgoEOVIID3ShNS4WheDYJgtQ4Fh8jYi1BwQEwLAMQhhT0TRn3zVoVBdbouW0IZvNfJpPSK3yiH8tSiGCsBQrICKopiuKErYBLeE1DL0yynLRX6AUtAnTwVHMe0nMKwgVBMHLf00GbnkKxrCGawLaOi1ImBC-BIEoTEOEuLTziSw4NMxRg0pMoozXY5DMxqMruSaFpb2cicxPKxBKsIaqjFqow-HGQNJRk1S9u6w7jtO87Ltoa6BqG0yXsy68RXqH0dHBKtPSFP5XQQNRuMhtzjHzdxQkDUhsGi+BihhggL1eizVE878ywFX0XxWosGUAgUuQI9bPQDSZ12iMgKE5nHOLK4U+eaFRBY8WxybMeofBWmbPsKqwodl0DGyWJWRuvGyvyrQqq1LfxXBUD9BDnG1IUEQFX01+nofrJFIMotYNi2bpsZtziRl8VzHeHAjSzd8n-v6MSjE-W9XdFbaKPDTdWwJa2OPe0sn0ID2ALKgwCIsHQZwhJaM4+TxmUJ9Q85D8MlL3A9jytsyuay75jB5bxCP8QUfEB7K9G5dwRjctp6RNrvwILyCYOLoflfe0e+mcnx6pGCfZ4nPpxMMCFy1fJl15RTeqJouiGMwJiS7e6oD-H4+-FPmeHJE6jk9BOJkT4RjbV2psT+3NsrLT6BOZyxgEHGHnhyXwwJISaHtAVFQs1eRQLhpsCMmJiH4FgaNFQ4I3gWFFPSPWPs9BOWZCCBB7gmRuTfEQgKJC2odXCpFA6lDbYASqiydwXpnJTmYeTI+rl3IviKlWR8PCWqEH2nuI67UToQBEZxX8K0MKQnnoTP6TkXJVkUZ5FRPlA5yyauQna5D1jyXEAkXR+j97vC-ICAU6gzBliBA3ORli3IeWUd5EIDMgA */
  createMachine(
    {
      context: {
        customer: null,
        addr: null,
        addrType: null,
        items: null,
        orderId: null,
        orderNote: "",
        paymentSession: null,
        shipCountry: null,
        shipId: null,
        shipName: null,
        shipType: null,
        total: null,
      },
      id: "onlineCheckout",
      initial: "init",
      on: {
        RESET: {
          actions: ["clearXState"],
          target: "init",
        },
        UPDATE_TOTAL: {
          actions: ["assignUpdatedTotal"],
        },
      },
      predictableActionArguments: true,
      schema: {
        context: {} as CheckoutContext,
        services: {} as {
          createOrder: { data: Order };
        },
      },
      states: {
        creatingOrder: {
          invoke: {
            id: "createOrder",
            onDone: {
              actions: ["pushOrderCreated", "clearXState"],
              target: "#onlineCheckout.init",
            },
            src: "createOrder",
          },
        },
        init: {
          on: {
            ADD_DEFAULTS: {
              actions: ["assignDefaults"],
              target: "selectAddr",
            },
          },
        },
        selectAddr: {
          on: {
            ADDR_SELECTED: {
              actions: ["assignAddrDetails"],
            },
            ADD_NOTE: {
              actions: ["assignNote"],
            },
            CREATE_ORDER: {
              target: "creatingOrder",
            },
            METHOD_SELECTED: {
              actions: ["assignPaymentMethod"],
            },
            SELECT_SHIPTYPE: {
              actions: ["assignShipDetails"],
            },
          },
        },
      },
    },
    {
      actions: {
        assignAddrDetails,
        assignDefaults,
        assignNote,
        assignPaymentMethod,
        assignShipDetails,
        assignUpdatedTotal,
        clearXState,
      },
    }
  );
